import { Theme } from "@mui/material";
import { MessageTypeColor } from "../../../../../types";

export const useFlashImageCollectionStyles =
  (isBot: boolean, variant: MessageTypeColor = "default", embedded = false) =>
  (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    const flashColors = theme.palette.flashColors;

    const containerMaxW = embedded ? "100%" : "420px";
    const imageMaxH = embedded ? "none" : "400px";
    const imageMinH = embedded ? "80px" : "200px";
    const padding = embedded ? 1.75 : 2;

    return {
      container: {
        position: "relative",
        width: "100%",
        maxWidth: containerMaxW,
        height: "auto",
        p: padding,
        borderRadius: 8,
        color: isDark ? "#fff" : "#000",
        fontFamily: "Red Hat Display",
        fontWeight: 500,
        display: "flex",
        flexDirection: "column",
        gap: "7px",
        background: flashColors.flashBg,
        border: "7.5px solid transparent",
        backgroundImage: `linear-gradient(${flashColors.flashBg}, ${flashColors.flashBg}), linear-gradient(135deg, ${flashColors.flash1} 0%, ${flashColors.flash2} 33%, ${flashColors.flash3} 66%, ${flashColors.flash4} 100%)`,
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",
        boxSizing: "border-box",
        overflow: "hidden",
        ...(embedded && { margin: 0 }),
      },
      titleRow: {
        display: "inline-block",
        alignSelf: "flex-start",
        width: "fit-content",
        maxWidth: "calc(100% - 16px)",
        boxSizing: "border-box",
        mt: 0,
        mr: 1,
        ml: 1,
        fontWeight: 700,
        fontSize: { xs: "0.9375rem", sm: "1.125rem", md: "1.1875rem" },
        color: "#FFFFFF",
        backgroundColor: theme.palette.mode === "dark" ? "#2E3192" : "#7B68EE",
        px: { xs: 2.5, sm: 2.75 },
        py: { xs: 1.25, sm: 1.125 },
        borderRadius: { xs: "14px", sm: "16px" },
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflow: "visible",
        ...(theme.palette.mode === "dark"
          ? {
              boxShadow:
                "0 2px 14px rgba(12, 16, 48, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.06)",
            }
          : {}),
        [theme.breakpoints.down("sm")]: {
          maxWidth: "calc(100% - 16px)",
          wordBreak: "normal",
          overflowWrap: "break-word",
          lineHeight: 1.4,
        },
        mb: 2,
      },
      titleTypography: {
        fontSize: "14px",
        lineHeight: 1.55,
        fontWeight: 700,
        color: "#FFFFFF",
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        "& .markdown": {
          fontSize: "14px",
          lineHeight: 1.55,
          whiteSpace: "normal",
        },
        "& .markdown > *": {
          marginBottom: 0,
        },
      },
      titleCollapsedTypography: {
        fontSize: "14px",
        lineHeight: 1.55,
        fontWeight: 700,
        color: "#FFFFFF",
        maxHeight: "6.2em",
        overflow: "hidden",
        "& .markdown": {
          fontSize: "14px",
          lineHeight: 1.55,
          whiteSpace: "normal",
        },
        "& .markdown > *": {
          marginBottom: 0,
        },
      },
      titleToggleContainer: {
        mt: 0.75,
        display: "flex",
        alignItems: "center",
        opacity: 0.95,
      },
      imageContainer: {
        position: "relative",
        width: "100%",
        maxWidth: containerMaxW,
        height: "auto",
        minHeight: imageMinH,
        ...(imageMaxH !== "none" && { maxHeight: imageMaxH }),
        borderRadius: 3,
        overflow: "hidden",
        cursor: "pointer",
        transition: "transform 0.2s ease-in-out",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
          transform: "scale(1.02)",
        },
      },
      image: {
        width: "100%",
        height: "auto",
        ...(imageMaxH !== "none" && { maxHeight: imageMaxH }),
        objectFit: "contain",
        borderRadius: 3,
      },
      descriptionText: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
        color: isDark ? "#fff" : "#000",
        fontFamily: "Red Hat Display",
        fontWeight: 500,
        mt: 2,
        mb: 2,
      },
      navigationButton: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        background: "rgba(0,0,0,0.5)",
        color: "#fff",
        borderRadius: "50%",
        width: 40,
        height: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          background: "rgba(0,0,0,0.7)",
        },
        "&.prev": {
          left: 16,
        },
        "&.next": {
          right: 16,
        },
      },
      imageCounter: {
        position: "absolute",
        bottom: 16,
        right: 16,
        background: flashColors.flashGradient,
        color: "#fff",
        padding: "4px 8px",
        borderRadius: 12,
        fontSize: "0.75rem",
        fontFamily: "Red Hat Display",
        fontWeight: 500,
      },
      timestamp: {
        fontSize: "12px",
        whiteSpace: "nowrap",
        alignSelf: "flex-end",
        color: isDark ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)",
        fontFamily: "Red Hat Display",
        fontWeight: 500,
        mt: 1,
      },
      expandButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        color: theme.palette.primary.main,
        background: "transparent",
        border: "none",
        padding: "4px 8px",
        borderRadius: 4,
        cursor: "pointer",
        fontSize: "0.875rem",
        fontWeight: 500,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          background: theme.palette.primary.main + "10",
        },
      },
      collapseToggle: {
        display: "flex",
        cursor: "pointer",
      },
      collapseToggleText: {
        mr: 0.5,
        color: "#06B6D4",
        fontFamily: "Red Hat Display",
        fontWeight: 500,
      },
      collapseToggleIcon: (isCollapsed: boolean) => ({
        fontSize: 18,
        transform: isCollapsed ? "rotate(0deg)" : "rotate(180deg)",
        transition: "transform 0.3s",
        color: "#06B6D4",
        fontFamily: "Red Hat Display",
        fontWeight: 500,
      }),
      modalContent: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: theme.spacing(3),
        [theme.breakpoints.up("md")]: {
          flexDirection: "row",
          gap: theme.spacing(3),
        },
      },
      modalImageContainer: {
        flex: 1,
        position: "relative",
        borderRadius: theme.shape.borderRadius,
        overflow: "hidden",
        [theme.breakpoints.up("md")]: {
          maxWidth: "60%",
        },
      },
      modalImage: {
        width: "100%",
        height: "100%",
        maxHeight: "80vh",
        objectFit: "contain",
      },
      modalTextContainer: {
        flex: 1,
        padding: theme.spacing(2),
        [theme.breakpoints.up("md")]: {
          maxWidth: "40%",
        },
      },
      modalTitle: {
        fontSize: "1.25rem",
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
      },
      modalDescription: {
        fontSize: "1rem",
        lineHeight: 1.6,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
      },
      modalMetadata: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: "0.875rem",
      },
    };
  };
